/**
 * 16×16 pixel glyphs (§4.9) for the places where the game should feel like a game: the tab bar,
 * results and landing. One colour (`currentColor`), drawn on the pixel grid so they stay crisp at
 * 16/24/32px. `#` = filled pixel.
 */

export const PIXEL_ICONS = {
  map: [
    '................',
    '.......##.......',
    '.....######.....',
    '...####..####...',
    '..###......###..',
    '..##........##..',
    '..##...##...##..',
    '..##..####..##..',
    '..##..####..##..',
    '..##...##...##..',
    '..##........##..',
    '..###......###..',
    '...####..####...',
    '.....######.....',
    '.......##.......',
    '................',
  ],
  armies: [
    '................',
    '.##.............',
    '.############...',
    '.###########....',
    '.##########.....',
    '.###########....',
    '.############...',
    '.##.............',
    '.##.............',
    '.##.............',
    '.##.............',
    '.##.............',
    '.##.............',
    '.##.............',
    '####............',
    '................',
  ],
  dispatch: [
    '................',
    '.......##.......',
    '.......##.......',
    '..##........##..',
    '...##......##...',
    '................',
    '.....######.....',
    '....########....',
    '...##########...',
    '##.##########.##',
    '................',
    '################',
    '................',
    '...##########...',
    '................',
    '......####......',
  ],
  intel: [
    '................',
    '................',
    '................',
    '.....######.....',
    '...##......##...',
    '..#....##....#..',
    '.#....####....#.',
    '#....##..##....#',
    '#....##..##....#',
    '.#....####....#.',
    '..#....##....#..',
    '...##......##...',
    '.....######.....',
    '................',
    '................',
    '................',
  ],
  team: [
    '................',
    '....####........',
    '...######..###..',
    '...######.#####.',
    '...######.#####.',
    '....####...###..',
    '................',
    '..########......',
    '.##########.###.',
    '.##########.####',
    '.##########.####',
    '.##########.####',
    '.##########.####',
    '................',
    '................',
    '................',
  ],
  trophy: [
    '................',
    '..############..',
    '#.############.#',
    '#.############.#',
    '#.############.#',
    '.#.##########.#.',
    '..#.########.#..',
    '.....######.....',
    '......####......',
    '.......##.......',
    '.......##.......',
    '.......##.......',
    '.....######.....',
    '....########....',
    '....########....',
    '................',
  ],
  crown: [
    '................',
    '................',
    '.#.....##.....#.',
    '.##...####...##.',
    '.###.######.###.',
    '.##############.',
    '.##############.',
    '.##############.',
    '.##..##..##..##.',
    '.##############.',
    '.##############.',
    '................',
    '................',
    '................',
    '................',
    '................',
  ],
  star: [
    '................',
    '.......##.......',
    '.......##.......',
    '......####......',
    '......####......',
    '################',
    '.##############.',
    '...##########...',
    '....########....',
    '....########....',
    '...####..####...',
    '...###....###...',
    '..##........##..',
    '..#..........#..',
    '................',
    '................',
  ],
  swords: [
    '................',
    '.##..........##.',
    '.###........###.',
    '..###......###..',
    '...###....###...',
    '....###..###....',
    '.....######.....',
    '......####......',
    '......####......',
    '.....######.....',
    '..#.###..###.#..',
    '..####....####..',
    '...##......##...',
    '..####....####..',
    '.##..........##.',
    '................',
  ],
  scroll: [
    '................',
    '..###########...',
    '.#############..',
    '.##.........##..',
    '..#.#######.#...',
    '..#.........#...',
    '..#.#######.#...',
    '..#.........#...',
    '..#.#####...#...',
    '..#.........#...',
    '..#.#######.#...',
    '..#.........#...',
    '.##.........##..',
    '.#############..',
    '..###########...',
    '................',
  ],
  gem: [
    '................',
    '................',
    '....########....',
    '...##..##..##...',
    '..##..####..##..',
    '.##############.',
    '..#..######..#..',
    '...#..####..#...',
    '....#..##..#....',
    '.....#.##.#.....',
    '......####......',
    '.......##.......',
    '................',
    '................',
    '................',
    '................',
  ],
  flag: [
    '................',
    '..##............',
    '..##########....',
    '..###########...',
    '..############..',
    '..###########...',
    '..##########....',
    '..##............',
    '..##............',
    '..##............',
    '..##............',
    '..##............',
    '..##............',
    '.####...........',
    '######..........',
    '................',
  ],
  help: [
    '................',
    '......####......',
    '......####......',
    '......####......',
    '......####......',
    '......####......',
    '......####......',
    '.......##.......',
    '.......##.......',
    '................',
    '................',
    '......####......',
    '......####......',
    '................',
    '................',
    '................',
  ],
  shield: [
    '................',
    '..############..',
    '..############..',
    '..##...##...##..',
    '..##...##...##..',
    '..##...##...##..',
    '..############..',
    '..############..',
    '..##...##...##..',
    '...##..##..##...',
    '...##..##..##...',
    '....##.##.##....',
    '.....######.....',
    '......####......',
    '.......##.......',
    '................',
  ],
} as const satisfies Record<string, readonly string[]>;

export type PixelIconName = keyof typeof PIXEL_ICONS;

/** SVG path data for a glyph: one 1×1 rect per run of filled pixels. */
export function pixelIconPath(rows: readonly string[]): string {
  let d = '';
  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      if (row[x] !== '#') {
        x++;
        continue;
      }
      let end = x;
      while (row[end] === '#') end++;
      d += `M${x} ${y}h${end - x}v1h${x - end}z`;
      x = end;
    }
  });
  return d;
}
